"use client";

import Link from "next/link";
import Image from "next/image";
import { useTranslation } from "react-i18next";
import { Clock, MapPin, Mail, Facebook, Instagram } from "lucide-react";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { routes } from "@/lib/routes";

const WHATSAPP_NUMBER = "5492944636295";
const GOOGLE_MAPS_URL = "https://www.google.com/maps/search/?api=1&query=Eduardo+Sfeir+2827+El+Bolsón+Río+Negro+Argentina";

const socialLinks = [
  { href: "https://www.facebook.com/bigsportselbolson", icon: Facebook, label: "Facebook" },
  { href: "https://www.instagram.com/bigsportselbolson", icon: Instagram, label: "Instagram" },
];

export default function Footer() {
  const { t } = useTranslation();

  const companyLinks = [
    { href: routes.home, label: t("header.home") },
    { href: routes.services.pitches, label: t("header.pitches") },
    { href: routes.services.tournaments, label: t("header.tournaments") },
    { href: routes.services.birthdays, label: t("header.birthdays") },
    { href: routes.services.otherSports, label: t("header.otherSports") },
    { href: routes.services.howToGetThere, label: t("header.howToGetThere") },
    { href: routes.contact, label: t("header.contact") },
  ];

  const services = [
    { label: t("footer.courtRental") },
    { label: t("footer.futsalTournaments") },
    { label: t("footer.birthdays") },
    { label: t("footer.basketballNewcom") },
    { label: t("footer.volleyball") },
  ];

  return (
    <footer className="relative bg-linear-to-b from-slate-900 to-slate-950 text-white">
      {/* Top accent line */}
      <div className="h-1 w-full bg-primary" />

      <div className="relative border-b border-white/6">
        <div className="mx-auto w-full max-w-6xl px-4 py-14 sm:px-6 sm:py-16 lg:px-8 lg:pr-20">
          <div className="grid gap-12 sm:grid-cols-2 sm:gap-x-10 sm:gap-y-12 lg:grid-cols-4 lg:gap-x-8">
            {/* Column 1 - Brand */}
            <div className="space-y-5">
              <Link
                href={routes.home}
                className="inline-flex items-center gap-3 transition-opacity hover:opacity-90"
              >
                <Image
                  src="/logo.png"
                  alt={t("common.brandName")}
                  width={48}
                  height={48}
                  sizes="48px"
                  loading="lazy"
                  className="h-10 w-10 object-contain sm:h-11 sm:w-11"
                />
                <span className="text-lg font-bold tracking-tight text-white sm:text-xl">
                  {t("common.brandName")}
                </span>
              </Link>
              <p className="max-w-sm text-[15px] leading-relaxed text-slate-300">
                {t("footer.description")}
              </p>
              <div className="flex gap-2.5 pt-1">
                {socialLinks.map(({ href, icon: Icon, label }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="flex h-11 w-11 min-h-[44px] min-w-[44px] items-center justify-center rounded-full border border-white/10 bg-white/[0.07] text-slate-100 shadow-sm transition hover:border-primary/40 hover:bg-primary/15 hover:text-white"
                  >
                    <Icon className="h-[22px] w-[22px]" strokeWidth={1.75} />
                  </a>
                ))}
              </div>
            </div>

            {/* Column 2 - Company */}
            <div>
              <h2 className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">
                {t("footer.company")}
              </h2>
              <ul className="mt-5 space-y-2.5 text-[15px]">
                {companyLinks.map((item) => (
                  <li key={item.label}>
                    <Link
                      href={item.href}
                      className="text-slate-200 transition-colors hover:text-white"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 3 - Our Services */}
            <div>
              <h2 className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">
                {t("footer.ourServices")}
              </h2>
              <ul className="mt-5 space-y-2.5 text-[15px]">
                {services.map((item) => (
                  <li key={item.label} className="text-slate-200">
                    {item.label}
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 4 - Contact */}
            <div className="space-y-7">
              <div className="flex gap-3">
                <Clock
                  className="mt-0.5 h-5 w-5 shrink-0 text-primary"
                  strokeWidth={2}
                  aria-hidden
                />
                <div>
                  <p className="font-semibold text-white">{t("footer.weAreOpen")}</p>
                  <p className="mt-1.5 text-[15px] leading-snug text-slate-300">
                    {t("footer.hours")}
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <MapPin
                  className="mt-0.5 h-5 w-5 shrink-0 text-primary"
                  strokeWidth={2}
                  aria-hidden
                />
                <div>
                  <p className="font-semibold text-white">{t("footer.ourLocation")}</p>
                  <a
                    href={GOOGLE_MAPS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1.5 block text-[15px] leading-snug text-slate-300 underline-offset-2 transition hover:text-white hover:underline"
                  >
                    {t("footer.location")}
                  </a>
                  <p className="mt-1 text-sm leading-relaxed text-slate-400">
                    {t("footer.locationNote")}
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center" aria-hidden>
                  <WhatsAppIcon size={20} className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-semibold text-white">{t("footer.sendMessage")}</p>
                  <a
                    href={`https://wa.me/${WHATSAPP_NUMBER}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1.5 block text-[15px] text-emerald-300/95 underline-offset-2 transition hover:text-emerald-200 hover:underline"
                  >
                    {t("footer.whatsapp")}
                  </a>
                  <a
                    href="mailto:club@bigsports.com.ar"
                    className="mt-2 inline-flex items-center gap-2 text-[15px] text-slate-300 transition hover:text-white"
                  >
                    <Mail className="h-4 w-4 shrink-0 text-slate-400" strokeWidth={2} />
                    club@bigsports.com.ar
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="safe-area-inset-bottom bg-slate-950/80">
        <div className="mx-auto flex w-full max-w-6xl flex-col items-center justify-between gap-3 px-4 py-4 sm:flex-row sm:px-6 lg:px-8">
          <span className="text-center text-sm text-slate-400 sm:text-left">
            {t("footer.copyright", { year: new Date().getFullYear() })}
          </span>
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <Link
              href={routes.terms}
              className="text-slate-400 transition hover:text-white"
            >
              {t("footer.terms")}
            </Link>
            <Link
              href={routes.privacy}
              className="text-slate-400 transition hover:text-white"
            >
              {t("footer.privacy")}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
